import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Section, Loading, Pill, useFetch, money } from "./_shared";
import { api } from "@/lib/api";
import {
  Activity, Wallet, Clock, Sparkles, ArrowRight, RefreshCw, ShieldAlert, Settings2, Check,
} from "lucide-react";

function renderSteps(text) {
  const lines = (text || "").split("\n");
  const out = [];
  lines.forEach((raw, i) => {
    const ln = raw.trimEnd();
    if (!ln.trim()) return;
    const h = ln.match(/^\s*(#{1,6})\s+(.*)$/);
    if (h) { out.push(<p key={i} className="mb-1 mt-1 text-sm font-semibold text-foreground">{h[2]}</p>); return; }
    const parts = ln.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    out.push(
      <p key={i} className="mb-1 text-sm leading-relaxed text-foreground">
        {parts.map((p, j) =>
          p.startsWith("**") && p.endsWith("**")
            ? <strong key={j}>{p.slice(2, -2)}</strong>
            : <span key={j}>{p}</span>
        )}
      </p>
    );
  });
  return out;
}

const RUNWAY_TONE = { green: "good", amber: "warn", red: "bad" };

function Cell({ label, value, tone = "default", hint, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testId}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-0.5 font-display text-xl font-semibold ${
        tone === "good" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : tone === "bad" ? "text-rose-600" : "text-foreground"
      }`}>{value}</p>
      {hint && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function OpsPulse() {
  const { data: p, loading, reload } = useFetch("/admin/command-center/ops-pulse");
  if (loading && !p) return <Section title="KI-Ops-Puls" testId="admin-cc-ops-pulse"><Loading /></Section>;
  if (!p) return null;
  const q = p.queues || {};
  const fin = p.finance;
  const tech = p.tech || {};
  const prodMissing = p.production?.missing || [];

  return (
    <Section
      title="KI-Ops-Puls"
      testId="admin-cc-ops-pulse"
      action={
        <Button size="sm" variant="ghost" className="gap-1 rounded-full text-xs" onClick={reload} data-testid="admin-cc-ops-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Aktualisieren
        </Button>
      }
    >
      <div className="mb-3 flex items-center gap-3 rounded-xl border border-border bg-secondary/40 p-3" data-testid="admin-cc-ops-health">
        <Activity className={`h-6 w-6 ${p.health.score >= 90 ? "text-emerald-600" : p.health.score >= 60 ? "text-amber-600" : "text-rose-600"}`} />
        <div>
          <p className="font-display text-2xl font-semibold text-foreground">{p.health.score}<span className="text-sm text-muted-foreground">/100</span></p>
          <p className="text-xs text-muted-foreground">{p.health.label}</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {tech.scheduler_fresh ? "Scheduler aktiv" : `Scheduler still (${tech.scheduler_minutes ?? ">60"} min)`}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {fin && (
          <>
            <Cell label="Master-Cash" value={money(fin.master_cash_cents)} testId="admin-cc-ops-cash" />
            <Cell
              label="Runway"
              value={fin.runway_months != null ? `${fin.runway_months} Mon.` : "—"}
              tone={RUNWAY_TONE[fin.runway_status] || "default"}
              testId="admin-cc-ops-runway"
            />
          </>
        )}
        <Cell label="Anomalien" value={tech.anomalies_open} tone={tech.anomalies_open ? "bad" : "good"} testId="admin-cc-ops-anomalies" />
        <Cell label="Eskalierter Support" value={q.support_escalated} tone={q.support_escalated ? "bad" : "good"} />
        <Cell label="Offene Meldungen" value={q.reports_pending} tone={q.reports_pending ? "bad" : "good"} />
        <Cell label="Coach-Verif." value={q.verif_pending} tone={q.verif_pending ? "warn" : "good"} />
        <Cell label="Finanz-Warnungen" value={q.finance_open} tone={q.finance_open ? "warn" : "good"} />
        <Cell label="Affiliate-Hold" value={q.affiliate_hold_count} hint={q.affiliate_hold_cents ? money(q.affiliate_hold_cents) : null} tone={q.affiliate_hold_count ? "info" : "good"} />
        <Cell label="Geplante Kampagnen" value={q.campaigns_scheduled} />
        <Cell label="Neu (7 T)" value={q.new_signups_7d} />
      </div>

      {prodMissing.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="admin-cc-ops-prod">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <p className="text-xs font-semibold text-amber-800">Produktions-Konfiguration unvollständig</p>
            <ul className="mt-1 list-disc pl-4 text-xs text-amber-700">
              {prodMissing.map((m) => <li key={m}>{m}</li>)}
            </ul>
          </div>
        </div>
      )}
    </Section>
  );
}

const DOMAIN_TONE = { bad: "bad", warn: "warn", info: "info", good: "good" };
const POLICY_LABEL = { auto: "Automatisch", confirm: "Mit Bestätigung", never: "Manuell" };
const POLICY_TONE = { auto: "info", confirm: "warn", never: "default" };

function TodoRow({ todo, onSolve, solving, onPolicy }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={`admin-cc-todo-${todo.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Pill tone={DOMAIN_TONE[todo.severity] || "default"}>{todo.domain_label}</Pill>
            <p className="truncate text-sm font-semibold text-foreground">{todo.title}</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{todo.detail}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <select
            value={todo.policy}
            onChange={(e) => onPolicy(todo.id, e.target.value)}
            className="rounded-full border border-border bg-background px-2 py-1 text-[11px] text-foreground"
            title="Autonomie dieses Workflows"
            data-testid={`admin-cc-todo-policy-${todo.id}`}
          >
            <option value="auto">Automatisch</option>
            <option value="confirm">Mit Bestätigung</option>
            <option value="never">Manuell</option>
          </select>
          <Button
            size="icon" variant="ghost" className="h-8 w-8 rounded-full text-primary"
            title="KI-Lösung" onClick={() => onSolve(todo.id)} disabled={solving}
            data-testid={`admin-cc-todo-solve-${todo.id}`}
          >
            <Sparkles className="h-4 w-4" />
          </Button>
          <Button
            size="sm" variant="outline" className="gap-1 rounded-full text-xs"
            onClick={() => navigate(todo.route)} data-testid={`admin-cc-todo-open-${todo.id}`}
          >
            Öffnen <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TodoBoard() {
  const { data, loading, reload, setData } = useFetch("/admin/command-center/todos");
  const [solveId, setSolveId] = useState(null);
  const [solution, setSolution] = useState(null);
  const [solveErr, setSolveErr] = useState(null);
  const [savedKey, setSavedKey] = useState(null);

  const solve = async (id) => {
    setSolveId(id); setSolveErr(null); setSolution(null);
    try {
      const { data: res } = await api.post("/admin/command-center/todo-solve", { id });
      setSolution(res);
      if (res.error) setSolveErr("KI derzeit nicht verfügbar — Fallback angezeigt.");
    } catch {
      setSolveErr("KI-Lösung konnte nicht erstellt werden.");
    } finally {
      setSolveId(null);
    }
  };

  const setPolicy = async (id, value) => {
    // optimistic update
    setData((d) => d ? { ...d, policies: { ...d.policies, [id]: value },
      todos: d.todos.map((t) => t.id === id ? { ...t, policy: value } : t) } : d);
    try {
      await api.put("/admin/command-center/workflow-policies", { policies: { [id]: value } });
      setSavedKey(id);
      setTimeout(() => setSavedKey((k) => (k === id ? null : k)), 1500);
    } catch {
      reload();
    }
  };

  const todos = data?.todos || [];

  return (
    <Section
      title={`Aufgaben-Board${data?.open_total ? ` · ${data.open_total} offen` : " · alles erledigt"}`}
      testId="admin-cc-todo-board"
      action={
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Settings2 className="h-3.5 w-3.5" /> Autonomie je Workflow
          </span>
          <Button size="sm" variant="ghost" className="gap-1 rounded-full text-xs" onClick={reload} data-testid="admin-cc-todo-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Aktualisieren
          </Button>
        </div>
      }
    >
      {loading && !data ? (
        <Loading />
      ) : todos.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="admin-cc-todo-empty">
          Aktuell keine offenen domänenübergreifenden Aufgaben. 🎉
        </p>
      ) : (
        <div className="space-y-2">
          {todos.map((t) => (
            <div key={t.id}>
              <TodoRow todo={t} onSolve={solve} solving={solveId === t.id} onPolicy={setPolicy} />
              {savedKey === t.id && (
                <p className="mt-1 flex items-center gap-1 pl-1 text-[11px] text-emerald-600" data-testid={`admin-cc-policy-saved-${t.id}`}>
                  <Check className="h-3 w-3" /> Autonomie gespeichert
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {solveId && <p className="mt-3 text-sm text-muted-foreground" data-testid="admin-cc-todo-solving">KI-Lösung wird erstellt …</p>}
      {solveErr && <p className="mt-2 text-xs text-amber-600" data-testid="admin-cc-todo-solve-error">{solveErr}</p>}
      {solution && (
        <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="admin-cc-todo-solution">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">KI-Lösung · {solution.title}</p>
            <Pill tone={POLICY_TONE[solution.policy] || "default"}>{POLICY_LABEL[solution.policy] || solution.policy}</Pill>
          </div>
          {renderSteps(solution.solution)}
          <p className="mt-2 text-[11px] text-muted-foreground">Erstellt: {new Date(solution.generated_at).toLocaleString("de-DE")}</p>
        </div>
      )}
    </Section>
  );
}
